<template>
  <div class="flex flex-col p-2 bg-white rounded min-h-screen my-2">
    <div class="flex flex-col animate-pulse bg-gray-100 min-h-screen" v-if="loading"></div>
    <div class="flex flex-col flex-1" v-else>
      <div class="flex flex-col" v-if="task">
        <div class="sticky top-0 bg-white z-10 pb-2 mb-2" style="margin: -0.5rem -0.5rem 0 -0.5rem; padding: 0.5rem 0.5rem 0 0.5rem; border-bottom: 1px solid #e5e7eb;">
          <b-row v-if="task">
            <b-col>
              <table class="min-w-full leading-normal mt-1 text-gray" style="border: solid 1px #dedede;">
                <thead class="text-md">
                <tr class="bg-gray-pale">
                  <td colspan="12" class="px-2 py-2 font-bold">Data Update Application Details</td>
                </tr>
                </thead>
                <tbody class="text-xs">
                <tr style="border: solid 1px #dedede;">
                  <td class="px-2 py-1 text-capitalize font-bold">Tracking Number</td>
                  <td class="px-2 py-1 text-capitalize">{{ task.trackingNumber || 'N/A' }}</td>
                </tr>
                <tr style="border: solid 1px #dedede;">
                  <td class="px-2 py-1 text-capitalize font-bold">Entity Name</td>
                  <td class="px-2 py-1 text-capitalize">{{ task.name || 'N/A' }}</td>
                </tr>
                <tr v-if="task.interimErn" style="border: solid 1px #dedede;">
                  <td class="px-2 py-1 text-capitalize font-bold">Interim ERN</td>
                  <td class="px-2 py-1 text-capitalize text-blue">{{ task.interimErn || 'N/A' }}</td>
                </tr>
                <tr style="border: solid 1px #dedede;">
                  <td class="px-2 py-1 text-capitalize font-bold">Type</td>
                  <td class="px-2 py-1 text-capitalize">{{ task.type || 'N/A' }}</td>
                </tr>
                <tr style="border: solid 1px #dedede;">
                  <td class="px-2 py-1 text-capitalize font-bold">Sub Type</td>
                  <td class="px-2 py-1 text-capitalize">{{ task.subType || 'N/A' }}</td>
                </tr>
                <tr style="border: solid 1px #dedede;">
                  <td class="px-2 py-1 text-capitalize font-bold">Form</td>
                  <td class="px-2 py-1 text-capitalize">{{ (form && form.name) || 'N/A' }}</td>
                </tr>
                <tr style="border: solid 1px #dedede;">
                  <td class="px-2 py-1 text-capitalize font-bold">Associate Register</td>
                  <td class="px-2 py-1 text-capitalize">{{ institutionName || 'N/A' }}</td>
                </tr>
                </tbody>
              </table>
            </b-col>
          </b-row>
          <div class="flex gap-2 justify-end pb-2 mt-2" id="action-buttons">
            <button
                class="btn-escalate"
                :disabled="!task"
                @click="escalate"
            >
              <i class="fa fa-arrow-up text-blue mr-1"></i> Escalate
            </button>
            <button
                class="btn-query"
                :disabled="!task"
                @click="query"
            >
              <i class="fa fa-question text-white mr-1"></i> Query
            </button>
            <button
                class="btn-approve"
                :disabled="!task"
                @click="showAreYouSure('approve')"
            >
              <i class="fas fa-check-circle text-white mr-1"></i> Approve
            </button>
          </div>
        </div>
        <NirDataUpdateTaskView :id="task.id" />
      </div>
      <div v-else class="flex items-center justify-center flex-1 text-gray-500" style="min-height: 300px;">
        <div class="flex flex-col text-center items-center">
          <i class="fa fa-inbox text-4xl mb-2"></i>
          <label>No tasks available</label>
        </div>
      </div>
    </div>

    <b-modal id="modal-query" hide-footer ref="queryApplication" title="Query Application">
      <b-col cols="12">
        <b-form class="w-full">
          <b-form-textarea
              v-model="queryMessage"
              type="text"
              placeholder="Give description of query"
              class="mt-1"
              required>
          </b-form-textarea>
          <p v-if="form && form.description" class="my-2 text-xs">{{form.description.length}}/{{maxText}}</p>
          <b-row>
            <b-col cols="12">
              <button type="button" class="bg-orange rounded-sm text-white text-sm px-3 py-1 mx-1 my-2 float-right" variant="primary"  @click="showAreYouSure('query')">
                Query<b-spinner v-if="loading" small class="ml-1"></b-spinner></button>
            </b-col>
          </b-row>
        </b-form>
      </b-col>
    </b-modal>
    <b-modal id="modal-a-escalate" hide-footer ref="escalateSubmissions" title="Escalate Application">
      <Escalation :taskId="task ? task.id : null" type="nir-data-update" permission="can_view_nir_data_update_escalations"/>
    </b-modal>

    <AppModal v-model="showFile" xl2>
      <div class="flex flex-col items-center justify-center p-4">
        <div class="bg-white shadow-lg rounded-lg overflow-hidden pdf-frame">
          <iframe
              v-if="fileBytes"
              :src="'data:application/pdf;base64,' + fileBytes + '#toolbar=0&navpanes=0&scrollbar=0'"
              class="w-full h-full border-0 pdf-iframe"
          ></iframe>
        </div>
      </div>
    </AppModal>

    <b-modal id="modal-are-you-sure" v-model="areYouSure" title="Confirm" hide-footer size="md" >
      <div class="p-1">
        <div class="flex items-start mb-4">
          <div class="ml-4 flex-1">
            <b-row>
              <b-col>
                <p class="text-sm">
                  <span class="h6">Are you sure you are ready to take this action ?</span>.<br/>
                  This action can only be reversed by your superiors. Only confirm if you are certain that this is the appropriate Final Decision
                </p>
                <p class="mt-3 text-sm italic">
                  <b>Note:</b><br/>
                  All actions performed on <b class="text-blue">OBRS</b> are recorded and details of the account that initiated are tagged to the action.
                </p>
              </b-col>
            </b-row>
            <b-row class="mt-3">
              <b-col cols="4">
                <button class="px-3 py-2 border-2 border-blue bg-white rounded-sm text-blue w-full" @click="areYouSure = false">
                  <i class="fa fa-minus-square text-blue mr-2"></i>Cancel</button>
              </b-col>
              <b-col cols="4"></b-col>
              <b-col cols="4">
                <button class="px-3 py-2 bg-green rounded-sm text-white w-full hover:bg-green-dark" @click="confirmedToExecuteNextAction">
                  <i class="fa fa-check-circle text-white mr-1"></i> Accept</button>
              </b-col>
            </b-row>
          </div>
        </div>
      </div>
    </b-modal>
  </div>
</template>

<script>
import jwtService from "@/views/home/services/non-individual/nir/utils/jwt";
import {api} from "@/app/services/api.service";
import AppModal from "@/views/home/services/non-individual/nir/components/AppModal.vue";
import { mapActions, mapGetters } from "vuex";
import NirDataUpdateTaskView from "@/views/home/services/non-individual/data-updates/NirDataUpdateTaskView.vue";
import Escalation from "@/views/home/commons/Escalation.vue";

export default {
  name: 'NirDataUpdateTaskQueue',
  components: {
    NirDataUpdateTaskView,
    Escalation,
    AppModal
  },
  data() {
    return {
      loading: true,
      approving: false,
      rejecting: false,
      showApproveConfirm: false,
      showRejectConfirm: false,
      showQueryModal: false,
      showEscalateModal: false,
      showFile: false,
      rejectionReason: '',
      queryMessage: '',
      escalateComment: '',
      fileToView: '',
      fileBytes: '',
      activeTab: 'form',
      task: null,
      form: null,
      formData: null,
      institutions: [],
      querying: false,
      escalating: false,
      selectedRegistrarId: null,
      registrarSearch: '',
      registrarLimit: 15,
      areYouSure: false,
      pendingAction: null,
      maxText: 1000
    };
  },
  computed: {
    ...mapGetters([
      "getRegistrars",
    ]),
    institutionName() {
      if (!this.task || !this.task.institutionId) return 'N/A';
      var inst = (this.institutions || []).find(function (i) { return i.id === this.task.institutionId; }.bind(this));
      return inst ? inst.name : 'N/A';
    },
  },
  mounted() {
    this.fetchInstitutions();
    this.fetch();
  },
  methods: {
    ...mapActions({
      fetchRegistrarsAction: "fetchRegistrars",
      searchRegistrarsAction: "searchRegistrars",
    }),
    fetchInstitutions() {
      var self = this;
      api().get('/nir/api/v1/institutions?page=1&limit=1000')
          .then(function (res) {
            if (res.data && res.data.data && res.data.data.content) {
              self.institutions = res.data.data.content;
            }
          })
          .catch(function () {});
    },
    fetch() {
      var self = this;
      var user = jwtService.getUser();
      var userId = user && user.userId ? user.userId : '';
      this.loading = true;
      api().get('/nir/api/v1/data-updates/next?userId=' + encodeURIComponent(userId))
          .then(function (res) {
            if (res.data && res.data.data && (!res.data.data.message || res.data.data.message.toLowerCase() !== 'no tasks in the queue')) {
              self.task = res.data.data;
              self.fetchForm(self.task.formId);
              self.fetchFormFields(self.task.formId);
            } else {
              self.task = null;
            }
            self.loading = false;
          })
          .catch(function () {
            self.loading = false;
            self.task = null;
          });
    },
    fetchForm(formId) {
      var self = this;
      if (!formId) return;
      api().get('/nir/api/v1/configurations/form?id=' + encodeURIComponent(formId))
          .then(function (res) {
            if (res.data && res.data.data) self.form = res.data.data;
          });
    },
    fetchFormFields(formId) {
      var self = this;
      if (!formId) return;
      api().get('/nir/api/v1/configurations/forms/fields?formId=' + encodeURIComponent(formId))
          .then(function (res) {
            if (res.data && res.data.data && res.data.data.data) {
              self.formData = res.data.data.data;
            }
          });
    },
    escalate() {
      this.$refs.escalateSubmissions.show();
    },
    query() {
      this.queryMessage = '';
      this.$refs.queryApplication.show();
    },
    approve() {
      this.showApproveConfirm = true;
    },
    showAreYouSure(action) {
      this.pendingAction = action;
      this.areYouSure = true;
    },
    confirmedToExecuteNextAction() {
      this.areYouSure = false;
      if (this.pendingAction === 'approve') {
        this.confirmApprove();
      } else if (this.pendingAction === 'reject') {
        this.confirmReject();
      } else if (this.pendingAction === 'query') {
        this.confirmQuery();
      }
      this.pendingAction = null;
    },
    reject() {
      this.rejectionReason = '';
      this.showRejectConfirm = true;
    },
    confirmApprove() {
      var self = this;
      this.showApproveConfirm = false;
      this.approving = true;
      api().post('/nir/api/v1/data-updates/approve', { applicationId: this.task ? this.task.id : null })
          .then(function () {
            self.approving = false;
            self.fetch();
          })
          .catch(function () {
            self.approving = false;
          });
    },
    async confirmQuery() {
      if (!this.task || !this.task.id || !this.queryMessage.trim()) return;
      var self = this;
      var senderId = null;
      try {
        const currentUser = JSON.parse(sessionStorage.getItem("current_user") || "{}");
        senderId = currentUser && currentUser.userId ? currentUser.userId : null;
      } catch (e) {
        senderId = null;
      }
      this.querying = true;
      var payload = {
        applicationId: this.task.id,
        message: this.queryMessage.trim(),
      };
      if (senderId) {
        payload.senderId = senderId;
      }
      api().post('/nir/api/v1/data-updates/query', payload)
          .then(function () {
            self.querying = false;
            self.queryMessage = '';
            self.$refs.queryApplication.hide();
            self.fetch();
          })
          .catch(function (error) {
            self.querying = false;
            const msg =
              (error && error.response && error.response.data && (error.response.data.message || error.response.data.error)) ||
              'Failed to send query. Please try again.';
            if (typeof self.errorToast === 'function') {
              self.errorToast('Query Error', msg);
            }
          });
    },
    confirmReject() {
      var self = this;
      this.showRejectConfirm = false;
      this.rejecting = true;
      api().post('/nir/api/v1/data-updates/approve', {
        applicationId: this.task ? this.task.id : null,
        reason: this.rejectionReason,
      })
          .then(function () {
            self.rejecting = false;
            self.rejectionReason = '';
            self.fetch();
          })
          .catch(function () {
            self.rejecting = false;
          });
    },
    viewFile(fileId) {
      var self = this;
      this.showFile = true;
      this.fileToView = fileId;
      api().post('/nir/api/v1/applications/attachment', { fileId: fileId })
          .then(function (res) {
            if (res.data && res.data.data && res.data.data.data) {
              self.fileBytes = res.data.data.data;
            }
          });
    },
    formatDate(dateStr) {
      if (!dateStr) return '—';
      return new Date(dateStr).toLocaleString();
    },
    taskStatusClass(status) {
      var map = {
        PENDING: 'bg-gray-100 text-gray-600',
        SUBMITTED: 'bg-gray-100 text-gray-800',
        ASSIGNED: 'bg-blue text-blue-800',
        APPROVED: 'bg-success-100 text-success-800',
        REJECTED: 'bg-danger-100 text-danger-800',
      };
      return map[status] || 'bg-gray-100 text-gray-600';
    },
    loadRegistrars() {
      this.fetchRegistrarsAction({
        permission: "can_view_nir_data_update_tasks",
        limit: this.registrarLimit,
      });
    },
    searchRegistrars() {
      if (!this.registrarSearch || !this.registrarSearch.trim()) {
        this.loadRegistrars();
        return;
      }
      this.searchRegistrarsAction({
        term: this.registrarSearch.trim(),
        permission: "can_view_nir_data_update_tasks",
      });
    },
    statusClass(status) {
      const map = {
        PENDING: 'bg-gray-100 text-gray-600',
        QUERIED: 'bg-orange-100 text-gray-600',
        SUBMITTED: 'bg-gray-100 text-gray-800',
        PREAPPROVAL: 'bg-success-50 text-success-600 border border-success-200',
        ASSIGNED: 'bg-blue-100 text-blue-800',
        APPROVED: 'bg-success-100 text-success-800',
        REJECTED: 'bg-danger-100 text-danger-800',
      };
      return map[status] || 'bg-gray-100 text-gray-600';
    },
  },
};
</script>

<style scoped>
.btn-escalate{
  @apply border border-blue bg-white rounded-sm text-blue text-sm px-3 py-1 mx-1;
}

.btn-escalate:hover{
  @apply text-blue-dark;
}

.btn-approve{
  @apply border bg-blue rounded-sm text-white text-sm px-3 py-1 mx-1 bg-white;
}

.btn-query{
  @apply border bg-blue rounded-sm text-white text-sm px-3 py-1 mx-1 bg-white;
}

.btn-reject{
  @apply border bg-blue rounded-sm text-white text-sm px-3 py-1 mx-1 bg-white;
}

.btn-approve:hover{
  @apply bg-green border-green text-white;
}

.btn-query:hover{
  @apply bg-orange border-orange text-white;
}

.btn-reject:hover{
  @apply bg-red border-red text-white;
}

.flex { display: flex; }
.flex-col { flex-direction: column; }
.p-2 { padding: 0.5rem; }
.pdf-frame { width: 595px; height: 842px; }
.pdf-iframe { width: 595px; height: 842px; }
.min-h-screen { min-height: 100vh; }
.animate-pulse { animation: pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite; }
@keyframes pulse { 0%, 100% { opacity: 1; } 50% { opacity: 0.5; } }
</style>
